import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bot, Copy, Loader, ShieldAlert, User } from 'lucide-react';
import analyticsV4Client from '../services/analyticsV4Client';
import ZombieSessionsPanel from './ZombieSessionsPanel';
import NormalSessionsPanel from './NormalSessionsPanel';

const formatDuration = (durationMs) => {
  const ms = Number(durationMs);
  if (!Number.isFinite(ms) || ms <= 0) return '0 min';
  const totalMinutes = Math.round(ms / 60000);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} h ${minutes} min`;
};

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const resolveTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const formatTs = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return date.toLocaleString();
  } catch {
    return date.toISOString();
  }
};

const toSelectValue = (value) => (value === null || value === undefined ? '' : String(value));

const AnalyticsV4OverviewPanel = () => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [rangeDays, setRangeDays] = useState(7);
  const [healthOk, setHealthOk] = useState(null);
  const [maxRangeDays, setMaxRangeDays] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [overview, setOverview] = useState(null);
  const [moderation, setModeration] = useState(null);
  const [funnel, setFunnel] = useState(null);

  // Replay tab state
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsLimit] = useState(50);
  const [sessionsStatus, setSessionsStatus] = useState('');
  const [sessionsEndReason, setSessionsEndReason] = useState('');
  const [sessionsResponse, setSessionsResponse] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const [simulatorsLoading, setSimulatorsLoading] = useState(false);
  const [simulatorsError, setSimulatorsError] = useState(null);
  const [simulatorsOptions, setSimulatorsOptions] = useState([]); // [{ name, agentIds: [] }]
  const [selectedSimulatorName, setSelectedSimulatorName] = useState('');

  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState(null);
  const [replayResponse, setReplayResponse] = useState(null);
  const [copiedSessionId, setCopiedSessionId] = useState(null);

  // Prompt Layer (3ra capa)
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsError, setPromptsError] = useState(null);
  const [promptsResponse, setPromptsResponse] = useState(null);
  const [promptsByTurnIndex, setPromptsByTurnIndex] = useState({});
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptModalTab, setPromptModalTab] = useState('system'); // 'system', 'messages', 'raw'

  // Simulador Público tab state
  const [publicStatsLoading, setPublicStatsLoading] = useState(false);
  const [publicStatsError, setPublicStatsError] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [publicPrompts, setPublicPrompts] = useState([]);
  const [publicPromptsLoading, setPublicPromptsLoading] = useState(false);
  const [publicPromptsError, setPublicPromptsError] = useState(null);
  const [publicPromptsSessionFilter, setPublicPromptsSessionFilter] = useState('');

  const timezone = useMemo(() => resolveTimezone(), []);

  const range = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    return {
      from: from.toISOString(),
      to: now.toISOString()
    };
  }, [rangeDays]);

  useEffect(() => {
    if (activeSubTab !== 'replay') return;
    // Reiniciar paginación al cambiar filtros/rango
    setSessionsPage(1);
  }, [activeSubTab, range.from, range.to, sessionsStatus, sessionsEndReason]);

  useEffect(() => {
    if (activeSubTab !== 'replay') return;

    // Cambiar simulador resetea sesión seleccionada.
    setSelectedSessionId(null);
    setReplayResponse(null);
    setReplayError(null);
    setReplayLoading(false);
    setSessionsPage(1);
  }, [activeSubTab, selectedSimulatorName]);

  useEffect(() => {
    if (activeSubTab !== 'replay') return;
    if (!selectedSimulatorName) return;
    const exists = simulatorsOptions.some((opt) => opt.name === selectedSimulatorName);
    if (!exists) setSelectedSimulatorName('');
  }, [activeSubTab, selectedSimulatorName, simulatorsOptions]);

  useEffect(() => {
    let isMounted = true;
    if (activeSubTab !== 'replay') return undefined;

    const loadSimulators = async () => {
      setSimulatorsLoading(true);
      setSimulatorsError(null);
      try {
        const res = await analyticsV4Client.get('/agents', {
          params: {
            from: range.from,
            to: range.to,
            timezone,
            status: sessionsStatus || undefined,
            endReason: sessionsEndReason || undefined
          }
        });
        const data = res?.data || {};

        if (!isMounted) return;

        if (!data?.success) {
          const msg = data?.message || 'No se pudo cargar simuladores';
          setSimulatorsError(msg);
          setSimulatorsOptions([]);
          return;
        }

        const agents = Array.isArray(data?.agents) ? data.agents : [];

        // Agrupar por nombreVisible (debe ser único en UI). Si hay duplicados, el filtro aplica a todos los agentId del mismo nombre.
        const grouped = new Map();
        for (const agent of agents) {
          const name = typeof agent?.nombreVisible === 'string' && agent.nombreVisible.trim() !== ''
            ? agent.nombreVisible.trim()
            : (typeof agent?.agentId === 'string' ? agent.agentId : '—');
          const agentId = typeof agent?.agentId === 'string' ? agent.agentId : null;
          if (!agentId) continue;
          const current = grouped.get(name) || [];
          if (!current.includes(agentId)) current.push(agentId);
          grouped.set(name, current);
        }

        const options = Array.from(grouped.entries())
          .map(([name, agentIds]) => ({ name, agentIds }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setSimulatorsOptions(options);
      } catch (err) {
        if (!isMounted) return;
        const msg = err?.response?.data?.message || err?.message || 'No se pudo cargar simuladores';
        setSimulatorsError(msg);
        setSimulatorsOptions([]);
      } finally {
        if (!isMounted) return;
        setSimulatorsLoading(false);
      }
    };

    loadSimulators();

    return () => {
      isMounted = false;
    };
  }, [activeSubTab, range.from, range.to, timezone, sessionsStatus, sessionsEndReason]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const isReplay = activeSubTab === 'replay';

      if (isReplay) {
        setSessionsLoading(true);
        setSessionsError(null);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const healthPromise = analyticsV4Client.get('/health');

        const dataPromise =
          activeSubTab === 'moderation'
            ? analyticsV4Client.get('/moderation', {
                params: { from: range.from, to: range.to, timezone }
              })
            : activeSubTab === 'funnel'
              ? analyticsV4Client.get('/sim/funnel', {
                  params: { from: range.from, to: range.to, timezone }
                })
              : activeSubTab === 'replay'
                ? analyticsV4Client.get('/sessions', {
                    params: {
                      from: range.from,
                      to: range.to,
                      timezone,
                      order: 'desc',
                      page: sessionsPage,
                      limit: sessionsLimit,
                      status: sessionsStatus || undefined,
                      endReason: sessionsEndReason || undefined
                    }
                  })
                : analyticsV4Client.get('/overview', {
                    params: { from: range.from, to: range.to, timezone }
                  });

        const [healthRes, dataRes] = await Promise.all([healthPromise, dataPromise]);

        if (!isMounted) return;

        const healthData = healthRes?.data || {};
        setHealthOk(Boolean(healthData?.ok));
        const maxDays = Number(healthData?.maxRangeDays);
        setMaxRangeDays(Number.isFinite(maxDays) ? maxDays : null);

        if (activeSubTab === 'moderation') {
          setModeration(dataRes?.data || null);
          setOverview(null);
          setFunnel(null);
          setSessionsResponse(null);
        } else if (activeSubTab === 'funnel') {
          setFunnel(dataRes?.data || null);
          setOverview(null);
          setModeration(null);
          setSessionsResponse(null);
        } else if (activeSubTab === 'replay') {
          setSessionsResponse(dataRes?.data || null);
          setOverview(null);
          setModeration(null);
          setFunnel(null);
        } else {
          setOverview(dataRes?.data || null);
          setModeration(null);
          setFunnel(null);
          setSessionsResponse(null);
        }
      } catch (err) {
        if (!isMounted) return;
        setHealthOk(false);

        // Si falla health, evitamos bloquear UX: mantenemos selector con valores por defecto.
        if (isMounted) {
          setMaxRangeDays(null);
        }

        const status = err?.response?.status;
        const rawMessage = err?.response?.data?.message || err?.message || 'Error al cargar Analytics V4';
        let message;
        if (status === 403) {
          message = 'Acceso no autorizado para ver Analytics V4.';
        } else if (status === 500) {
          message = 'Servicio de Analytics no disponible. Verifica que el servicio en 3006 esté levantado (DESA) o que el proxy /analytics esté configurado (PROD).';
        } else if (status === 400) {
          const match = String(rawMessage).match(/max\s+(\d+)\s+days/i);
          const maxDaysFromServer = match ? Number(match[1]) : null;
          const effectiveMax = Number.isFinite(maxDaysFromServer) ? maxDaysFromServer : Number.isFinite(Number(maxRangeDays)) ? Number(maxRangeDays) : null;
          message = effectiveMax
            ? `Rango excede el máximo permitido por este entorno: ${effectiveMax} días. Sugerencia: usa 7 o 30 días.`
            : 'Rango excede el máximo permitido por este entorno. Sugerencia: usa 7 o 30 días.';
        } else {
          message = rawMessage;
        }

        if (activeSubTab === 'replay') {
          setSessionsError({ status, message });
          setSessionsResponse(null);
        } else {
          setError({ status, message });

          if (activeSubTab === 'moderation') {
            setModeration(null);
          } else if (activeSubTab === 'funnel') {
            setFunnel(null);
          } else {
            setOverview(null);
          }
        }
      } finally {
        if (!isMounted) return;
        if (activeSubTab === 'replay') {
          setSessionsLoading(false);
        } else {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [activeSubTab, range.from, range.to, timezone, sessionsPage, sessionsLimit, sessionsStatus, sessionsEndReason]);

  useEffect(() => {
    let isMounted = true;
    if (activeSubTab !== 'replay') return undefined;
    if (!selectedSessionId) {
      setReplayResponse(null);
      setReplayError(null);
      setReplayLoading(false);
      setPromptsResponse(null);
      setPromptsError(null);
      setPromptsLoading(false);
      setPromptsByTurnIndex({});
      return undefined;
    }

    const loadReplay = async () => {
      setReplayLoading(true);
      setReplayError(null);
      setReplayResponse(null);
      try {
        const res = await analyticsV4Client.get(`/sessions/${encodeURIComponent(selectedSessionId)}/replay`);
        if (!isMounted) return;
        setReplayResponse(res?.data || null);
      } catch (err) {
        if (!isMounted) return;
        const status = err?.response?.status;
        const details = err?.response?.data?.details;
        const detailsText = details ? `\n\nDetalles: ${JSON.stringify(details, null, 2)}` : '';
        if (status === 404) {
          setReplayError({ status, message: 'Sesión no encontrada' });
        } else if (status === 403) {
          setReplayError({ status, message: 'Acceso no autorizado para ver Replay.' });
        } else if (status === 409) {
          const rawMessage = err?.response?.data?.message || 'Sesión inválida para replay (faltan eventos canónicos)';
          setReplayError({ status, message: `${rawMessage}${detailsText}` });
        } else {
          const rawMessage = err?.response?.data?.message || err?.message || 'Error al cargar Replay';
          setReplayError({ status, message: `${rawMessage}${detailsText}` });
        }
      } finally {
        if (!isMounted) return;
        setReplayLoading(false);
      }
    };

    const loadPrompts = async () => {
      setPromptsLoading(true);
      setPromptsError(null);
      setPromptsResponse(null);
      setPromptsByTurnIndex({});
      try {
        const res = await analyticsV4Client.get(`/sessions/${encodeURIComponent(selectedSessionId)}/prompts`);
        if (!isMounted) return;
        const data = res?.data || {};
        setPromptsResponse(data);

        // Crear map turnIndex -> prompt
        const prompts = Array.isArray(data?.prompts) ? data.prompts : [];
        const map = {};
        prompts.forEach((p) => {
          if (Number.isFinite(p.turnIndex)) {
            map[p.turnIndex] = p;
          }
        });
        setPromptsByTurnIndex(map);
      } catch (err) {
        if (!isMounted) return;
        // Fail silently si no hay prompts (feature opcional)
        console.warn('[Replay] No prompts available:', err.message);
        setPromptsError(err?.response?.data?.message || err?.message || 'No prompts');
      } finally {
        if (!isMounted) return;
        setPromptsLoading(false);
      }
    };

    loadReplay();
    loadPrompts();

    return () => {
      isMounted = false;
    };
  }, [activeSubTab, selectedSessionId]);

  const availableRangeOptions = useMemo(() => {
    const max = Number(maxRangeDays);
    const allow90 = Number.isFinite(max) ? max >= 90 : true;
    return allow90 ? [7, 30, 90] : [7, 30];
  }, [maxRangeDays]);

  useEffect(() => {
    if (!Number.isFinite(Number(maxRangeDays))) return;
    const max = Number(maxRangeDays);
    if (rangeDays <= max) return;

    const fallback = availableRangeOptions
      .filter((d) => d <= max)
      .sort((a, b) => b - a)[0];
    setRangeDays(fallback || 7);
  }, [maxRangeDays, rangeDays, availableRangeOptions]);

  useEffect(() => {
    if (activeSubTab !== 'public') return;
    let isMounted = true;

    const loadPublicStats = async () => {
      setPublicStatsLoading(true);
      setPublicStatsError(null);
      try {
        const res = await analyticsV4Client.get('/public-trial/stats');
        if (isMounted) setPublicStats(res.data);
      } catch (err) {
        if (isMounted) setPublicStatsError(err?.response?.data?.message || err?.message || 'Error al cargar estadísticas');
      } finally {
        if (isMounted) setPublicStatsLoading(false);
      }
    };

    const loadPublicPrompts = async (sessionFilter = '') => {
      setPublicPromptsLoading(true);
      setPublicPromptsError(null);
      try {
        const params = { limit: 30 };
        if (sessionFilter) params.sessionId = sessionFilter;
        const res = await analyticsV4Client.get('/public-trial/prompts', { params });
        if (isMounted) setPublicPrompts(Array.isArray(res.data?.prompts) ? res.data.prompts : []);
      } catch (err) {
        if (isMounted) setPublicPromptsError(err?.response?.data?.message || err?.message || 'Error al cargar prompts');
      } finally {
        if (isMounted) setPublicPromptsLoading(false);
      }
    };

    loadPublicStats();
    loadPublicPrompts();
    return () => { isMounted = false; };
  }, [activeSubTab]);

  const totals = overview?.totals || {};
  const averages = overview?.averages || {};

  const totalSessions = safeNumber(totals.totalSessions);
  const completed = safeNumber(totals.completed);
  const active = safeNumber(totals.active);
  const abandoned = safeNumber(totals.abandoned);
  const avgTurns = safeNumber(averages.avgTurns);
  const avgDurationMs = safeNumber(averages.avgDurationMs);

  const moderationSessions = moderation?.sessions || {};
  const totalSessionsWithModeration = safeNumber(moderationSessions.totalSessionsWithModeration);
  const sessionsWithWarn = safeNumber(moderationSessions.sessionsWithWarn);
  const warnSessionRate =
    typeof moderationSessions.warnSessionRate === 'number'
      ? moderationSessions.warnSessionRate
      : totalSessionsWithModeration > 0
        ? sessionsWithWarn / totalSessionsWithModeration
        : 0;

  const warnByReason = Array.isArray(moderation?.warnByReason) ? moderation.warnByReason : [];
  const warnByPhaseTurn = Array.isArray(moderation?.warnByPhaseTurn) ? moderation.warnByPhaseTurn : [];
  const maxWarnReasonCount = warnByReason.reduce((acc, item) => {
    const count = safeNumber(item?.count);
    return Math.max(acc, count);
  }, 0);

  const funnelSteps = Array.isArray(funnel?.funnel) ? funnel.funnel : [];
  const dropoffRows = Array.isArray(funnel?.dropoff) ? funnel.dropoff : [];
  const timeByPhaseRows = Array.isArray(funnel?.timeByPhaseMs) ? funnel.timeByPhaseMs : [];
  const startedFunnel = safeNumber(funnelSteps?.[0]?.count);

  const funnelPhaseSource = typeof funnel?.notes?.phaseSource === 'string' ? funnel.notes.phaseSource : '—';
  const funnelPhaseSourceLabel = funnelPhaseSource === 'logs_derived' ? 'logs derivados' : funnelPhaseSource;

  const funnelSourceBadgeClass =
    funnelPhaseSource === 'milestones'
      ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
      : funnelPhaseSource === 'logs_derived'
        ? 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/20'
        : 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-500/20';

  const conversationStepLabel = (step) => {
    switch (step) {
      case 'started':
        return 'Iniciadas';
      case 'brief_passed':
        return 'Recibieron escenario';
      case 'phase_1':
        return 'Entraron en diálogo';
      case 'phase_2':
        return 'Llegaron a punto de decisión';
      case 'completed':
        return 'Cierre de simulación';
      default:
        return typeof step === 'string' && step.trim() !== '' ? step : '—';
    }
  };

  const conversationPhaseLabel = (phase) => {
    switch (phase) {
      case 'BRIEF':
        return 'Escenario';
      case 'PHASE_1':
        return 'Diálogo';
      case 'PHASE_2':
        return 'Punto de decisión';
      case 'TOTAL':
        return 'Total';
      default:
        return typeof phase === 'string' && phase.trim() !== '' ? phase : '—';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-gray-800">Analytics V4</h2>
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${healthOk ? 'bg-green-100 text-green-700' : healthOk === false ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
            {healthOk ? 'OK' : healthOk === false ? 'Offline' : '—'}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div>
            <select
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
              className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {availableRangeOptions.includes(7) && <option value={7}>Últimos 7 días</option>}
              {availableRangeOptions.includes(30) && <option value={30}>Últimos 30 días</option>}
              {availableRangeOptions.includes(90) && <option value={90}>Últimos 90 días</option>}
            </select>
          </div>

          <div className="text-xs text-gray-400">{timezone}</div>
        </div>
      </div>

      {/* Navbar móvil: select */}
      <div className="md:hidden mb-3">
        <select
          value={activeSubTab}
          onChange={(e) => setActiveSubTab(e.target.value)}
          className="w-full px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="overview">Overview</option>
          <option value="moderation">Moderation</option>
          <option value="funnel">Progreso</option>
          <option value="replay">Replay</option>
          <option value="zombie">Zombie Sessions</option>
          <option value="normal">Normal Sessions</option>
          <option value="public">Simulador Público</option>
        </select>
      </div>
      {/* Navbar desktop: tabs */}
      <div className="hidden md:block mb-4">
        <div className="flex gap-1 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveSubTab('overview')}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'overview'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('moderation')}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'moderation'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Moderation
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('funnel')}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'funnel'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Progreso
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('replay')}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'replay'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Replay
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('zombie')}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'zombie'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Zombie Sessions
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('normal')}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'normal'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Normal Sessions
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('public')}
            className={`whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'public'
                ? 'border-purple-600 text-purple-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Simulador Público
          </button>
        </div>
      </div>

      {/* Controles Replay - solo visible cuando activeSubTab === 'replay' */}
      {activeSubTab === 'replay' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Simulador</label>
            <select
              value={selectedSimulatorName}
              onChange={(e) => setSelectedSimulatorName(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">Todos</option>
              {simulatorsOptions.map((opt) => (
                <option key={opt.name} value={opt.name}>{opt.name}</option>
              ))}
            </select>
            {simulatorsLoading && (
              <p className="text-xs text-gray-500 mt-1">Cargando simuladores…</p>
            )}
            {!simulatorsLoading && simulatorsError && (
              <p className="text-xs text-red-700 mt-1">{simulatorsError}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={toSelectValue(sessionsStatus)}
              onChange={(e) => setSessionsStatus(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">Todos</option>
              <option value="completed">completed</option>
              <option value="closed">closed</option>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="armed_to_close">armed_to_close</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">End reason</label>
            <input
              value={sessionsEndReason}
              onChange={(e) => setSessionsEndReason(e.target.value)}
              placeholder="Ej: MAX_TURNS_REACHED"
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
          </div>
        </div>
      )}

      {activeSubTab !== 'replay' && loading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-blue-600" size={40} />
        </div>
      )}

      {activeSubTab !== 'replay' && !loading && error && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-700 mt-0.5" size={18} />
            <div>
              <p className="text-sm font-semibold text-red-800">No se pudo cargar Analytics V4</p>
              <p className="text-sm text-red-700">
                {error.status ? `(${error.status}) ` : ''}{error.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {activeSubTab !== 'replay' && !loading && !error && (
        <>
          {activeSubTab === 'overview' && (
            <>
              {totalSessions === 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-800">No hay datos para el rango seleccionado.</p>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-4">
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Sesiones totales</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{totalSessions}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Completadas</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{completed}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Activas</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{active}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Abandonadas</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{abandoned}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Turns prom.</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{avgTurns.toFixed(1)}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Duración prom.</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{formatDuration(avgDurationMs)}</p>
                </div>
              </div>
            </>
          )}

          {activeSubTab === 'moderation' && (
            <>
              {totalSessionsWithModeration === 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-800">
                    No hay datos de moderación para el rango seleccionado.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-4 mb-4">
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">% WARN</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{(safeNumber(warnSessionRate) * 100).toFixed(1)}%</p>
                  <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">{sessionsWithWarn}/{totalSessionsWithModeration}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Con WARN</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{sessionsWithWarn}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600">Con moderación</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{totalSessionsWithModeration}</p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-800">WARN por reason</h3>
                </div>

                {warnByReason.length === 0 ? (
                  <div className="p-4">
                    <p className="text-sm text-gray-600">Sin WARNs en el rango seleccionado.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reason
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Count
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Distribución
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {warnByReason.map((row, idx) => {
                          const count = safeNumber(row?.count);
                          const ratio = maxWarnReasonCount > 0 ? count / maxWarnReasonCount : 0;
                          const reason = typeof row?.reason === 'string' && row.reason.trim() !== '' ? row.reason : 'UNKNOWN';

                          return (
                            <tr key={`${reason}-${idx}`}>
                              <td className="px-4 py-3 text-sm text-gray-900">{reason}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{count}</td>
                              <td className="px-4 py-3">
                                <div className="w-full h-2 bg-gray-100 rounded">
                                  <div
                                    className="h-2 bg-blue-600 rounded"
                                    style={{ width: `${Math.round(ratio * 100)}%` }}
                                  />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {warnByPhaseTurn.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-800">WARN por fase/turno</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fase
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Turno
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {warnByPhaseTurn.map((row, idx) => {
                          const phaseId = typeof row?.phaseId === 'string' && row.phaseId.trim() !== '' ? row.phaseId : 'UNKNOWN';
                          const turn = row?.turn ?? '—';
                          const count = safeNumber(row?.count);

                          return (
                            <tr key={`${phaseId}-${turn}-${idx}`}>
                              <td className="px-4 py-3 text-sm text-gray-900">{phaseId}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{turn}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{count}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {activeSubTab === 'funnel' && (
            <>
              {startedFunnel === 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-800">Sin datos para el rango seleccionado.</p>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Progreso de la conversación</h3>
                      <p className="text-xs text-gray-600 mt-1">
                        Estos indicadores reflejan hitos conversacionales detectados en las simulaciones. No representan fases académicas ni pasos rígidos.
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${funnelSourceBadgeClass}`}>
                        Fuente: {funnelPhaseSourceLabel}
                      </span>
                      <span className="text-xs text-gray-500">
                        Sesiones analizadas: <span className="font-medium text-gray-700">{safeNumber(funnel?.notes?.sessionsAnalyzed)}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {funnelSteps.length === 0 ? (
                  <div className="p-4">
                    <p className="text-sm text-gray-600">Sin datos de funnel.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hito</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Conteo</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% sobre iniciadas</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {funnelSteps.map((row, idx) => {
                          const label = conversationStepLabel(row?.step);
                          const count = safeNumber(row?.count);
                          const pct = typeof row?.pctOfStarted === 'number' ? row.pctOfStarted : 0;
                          return (
                            <tr key={`${row?.step || 'step'}-${idx}`}>
                              <td className="px-4 py-3 text-sm text-gray-900">{label}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{count}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{(safeNumber(pct) * 100).toFixed(1)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-800">Drop-off entre hitos</h3>
                </div>

                {dropoffRows.length === 0 ? (
                  <div className="p-4">
                    <p className="text-sm text-gray-600">Sin datos de drop-off.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Desde</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hacia</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Perdidas</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">% drop</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {dropoffRows.map((row, idx) => {
                          const from = conversationStepLabel(row?.from);
                          const to = conversationStepLabel(row?.to);
                          const lost = safeNumber(row?.lost);
                          const dropPct = typeof row?.dropPct === 'number' ? row.dropPct : 0;
                          return (
                            <tr key={`${from}-${to}-${idx}`}>
                              <td className="px-4 py-3 text-sm text-gray-900">{from}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{to}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{lost}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{(safeNumber(dropPct) * 100).toFixed(1)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-800">Tiempo por etapa</h3>
                </div>

                {timeByPhaseRows.length === 0 ? (
                  <div className="p-4">
                    <p className="text-sm text-gray-600">Sin datos de tiempos por fase.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Etapa</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Promedio</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Mediana</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {timeByPhaseRows.map((row, idx) => {
                          const phase = conversationPhaseLabel(row?.phase);
                          const avg = safeNumber(row?.avgMs);
                          const med = safeNumber(row?.medianMs);
                          return (
                            <tr key={`${phase}-${idx}`}>
                              <td className="px-4 py-3 text-sm text-gray-900">{phase}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatDuration(avg)}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatDuration(med)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {activeSubTab === 'replay' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Panel izquierdo: selector de sesiones */}
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">Sesiones</h3>
              <p className="text-xs text-gray-600 mt-1">Orden: createdAt DESC</p>
            </div>

            {sessionsError && (
              <div className="p-4">
                <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-red-700 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm font-semibold text-red-800">No se pudo cargar sesiones</p>
                      <p className="text-sm text-red-700">
                        {sessionsError.status ? `(${sessionsError.status}) ` : ''}{sessionsError.message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!sessionsError && sessionsLoading && (
              <div className="p-4 space-y-3 animate-pulse">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-3">
                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                ))}
              </div>
            )}

            {!sessionsError && !sessionsLoading && (
              <>
                {(() => {
                  const rawSessions = Array.isArray(sessionsResponse?.sessions) ? sessionsResponse.sessions : [];
                  const selectedGroup = selectedSimulatorName
                    ? simulatorsOptions.find((opt) => opt.name === selectedSimulatorName)
                    : null;
                  const allowedAgentIds = selectedGroup ? new Set(selectedGroup.agentIds || []) : null;
                  const visibleSessions = allowedAgentIds
                    ? rawSessions.filter((s) => allowedAgentIds.has(s.agentId))
                    : rawSessions;

                  if (rawSessions.length === 0) {
                    return (
                      <div className="p-4">
                        <p className="text-sm text-gray-600">Sin sesiones para los filtros seleccionados.</p>
                      </div>
                    );
                  }

                  if (visibleSessions.length === 0) {
                    return (
                      <div className="p-4">
                        <p className="text-sm text-gray-600">Sin sesiones para el simulador seleccionado.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="p-2 space-y-2">
                      {visibleSessions.map((s) => {
                        const isSelected = s.sessionId && s.sessionId === selectedSessionId;
                        return (
                          <div
                            key={s.sessionId}
                            onClick={() => setSelectedSessionId(s.sessionId)}
                            className={`w-full text-left border rounded-lg p-3 transition-colors cursor-pointer ${
                              isSelected
                                ? 'border-blue-200 bg-blue-50'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="text-sm font-semibold text-gray-900 truncate"
                                    title={s.sessionId}
                                  >
                                    {s.sessionId}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (!s.sessionId) return;
                                      const text = String(s.sessionId);
                                      if (navigator?.clipboard?.writeText) {
                                        navigator.clipboard.writeText(text);
                                      }
                                      setCopiedSessionId(text);
                                      window.setTimeout(() => {
                                        setCopiedSessionId((current) => (current === text ? null : current));
                                      }, 1200);
                                    }}
                                    className="inline-flex items-center justify-center p-1 rounded hover:bg-white"
                                    aria-label="Copiar sessionId"
                                  >
                                    <Copy size={14} className={copiedSessionId === s.sessionId ? 'text-blue-700' : 'text-gray-500'} />
                                  </button>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">{formatTs(s.createdAt)}</p>
                                {s.userName ? (
                                  <p className="text-xs text-gray-600 mt-1 truncate" title={String(s.userName)}>
                                    Usuario: <span className="font-medium text-gray-900">{String(s.userName)}</span>
                                  </p>
                                ) : null}
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-600">status</p>
                                <p className="text-sm font-medium text-gray-900">{s.status || '—'}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 mt-3">
                              <div className="text-xs text-gray-600">
                                aiTurnCount: <span className="font-medium text-gray-900">{safeNumber(s.aiTurnCount)}</span>
                              </div>
                              <div className="text-xs text-gray-600">
                                completedTurns: <span className="font-medium text-gray-900">{safeNumber(s.completedTurns)}</span>
                              </div>
                              <div className="text-xs text-gray-600 col-span-2">
                                endReason: <span className="font-medium text-gray-900">{s.endReason || '—'}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Paginación */}
                <div className="px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between gap-3">
                  <div className="text-xs text-gray-600">
                    Página <span className="font-medium text-gray-900">{safeNumber(sessionsResponse?.pagination?.page) || sessionsPage}</span>
                    {Number.isFinite(Number(sessionsResponse?.pagination?.total)) && (
                      <>
                        {' '}· Total: <span className="font-medium text-gray-900">{safeNumber(sessionsResponse?.pagination?.total)}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSessionsPage((p) => Math.max(1, p - 1))}
                      disabled={sessionsPage <= 1}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-700 disabled:opacity-50 hover:bg-gray-50"
                    >
                      Anterior
                    </button>
                    <button
                      type="button"
                      onClick={() => setSessionsPage((p) => p + 1)}
                      disabled={!sessionsResponse?.pagination?.hasMore}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-700 disabled:opacity-50 hover:bg-gray-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Panel derecho: replay */}
          <div className="lg:col-span-2 border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">Replay</h3>
              <p className="text-xs text-gray-600 mt-1">
                {selectedSessionId ? (
                  <>Sesión: <span className="font-medium text-gray-900">{selectedSessionId}</span></>
                ) : (
                  'Selecciona una sesión para ver el replay.'
                )}
              </p>
            </div>

            {!selectedSessionId && (
              <div className="p-4">
                <p className="text-sm text-gray-600">No hay sesión seleccionada.</p>
              </div>
            )}

            {selectedSessionId && replayLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader className="animate-spin text-blue-600" size={40} />
              </div>
            )}

            {selectedSessionId && !replayLoading && replayError && (
              <div className="p-4">
                <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-red-700 mt-0.5" size={18} />
                    <div>
                      <p className="text-sm font-semibold text-red-800">No se pudo cargar Replay</p>
                      <p className="text-sm text-red-700">
                        {replayError.status ? `(${replayError.status}) ` : ''}{replayError.message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedSessionId && !replayLoading && !replayError && (
              <div className="p-4 space-y-4">
                {(() => {
                  const items = Array.isArray(replayResponse?.replay) ? replayResponse.replay : [];
                  if (items.length === 0) {
                    return (
                      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                        <p className="text-sm text-amber-800">Sin eventos de replay</p>
                      </div>
                    );
                  }
                  const briefItem = items.find((it) => it?.eventType === 'SYSTEM_BRIEF');
                  const kickoffItem = items.find((it) => it?.eventType === 'SYSTEM_KICKOFF');
                  const timelineItems = items
                    .filter((it) => it?.eventType === 'USER_TURN' || it?.eventType === 'AI_RESPONSE' || it?.eventType === 'MODERATION')
                    .slice()
                    .sort((a, b) => {
                      const sa = Number.isFinite(Number(a?.sequence)) ? Number(a.sequence) : Number.POSITIVE_INFINITY;
                      const sb = Number.isFinite(Number(b?.sequence)) ? Number(b.sequence) : Number.POSITIVE_INFINITY;
                      return sa - sb;
                    });

                  const getModerationDecision = (it) => (
                    it?.decision ||
                    it?.action ||
                    it?.moderation?.decision ||
                    it?.moderation?.action ||
                    it?.moderation?.result ||
                    null
                  );

                  const getModerationReason = (it) => (
                    it?.reason ||
                    it?.moderation?.reason ||
                    it?.moderation?.message ||
                    it?.moderation?.details ||
                    null
                  );

                  const resolveTurnLabel = (it) => {
                    const t = it?.turn ?? it?.parentTurn;
                    if (t === null || t === undefined) return '—';
                    const n = Number(t);
                    return Number.isFinite(n) ? String(n) : String(t);
                  };

                  const resolveSequenceLabel = (it) => {
                    const s = it?.sequence;
                    if (s === null || s === undefined) return '—';
                    const n = Number(s);
                    return Number.isFinite(n) ? String(n) : String(s);
                  };

                  const DecisionBadge = ({ decision }) => {
                    const raw = typeof decision === 'string' ? decision : (decision == null ? '' : String(decision));
                    const normalized = raw.trim().toLowerCase();

                    const style = (() => {
                      if (normalized === 'warning' || normalized === 'warn') {
                        return 'bg-amber-100 text-amber-800 border-amber-200';
                      }
                      if (normalized === 'block' || normalized === 'blocked' || normalized === 'end_session' || normalized === 'pause_session') {
                        return 'bg-red-100 text-red-800 border-red-200';
                      }
                      if (normalized === 'allow' || normalized === 'none') {
                        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
                      }
                      return 'bg-gray-100 text-gray-700 border-gray-200';
                    })();

                    return (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${style}`}>
                        {raw || '—'}
                      </span>
                    );
                  };

                  return (
                    <>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800">Brief + Kickoff</h4>
                        </div>
                        <div className="p-4">
                          {briefItem?.text ? (
                            <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                              {briefItem.text}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 italic">Brief no disponible para esta sesión</p>
                          )}


                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-700 mb-2">Kickoff (pregunta inicial)</p>
                            {kickoffItem?.text ? (
                              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                                {kickoffItem.text}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-600 italic">Kickoff no disponible para esta sesión</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800">Conversación</h4>
                        </div>

                        {timelineItems.length === 0 ? (
                          <div className="p-4">
                            <p className="text-sm text-gray-600">Sin conversación para esta sesión.</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-200">
                            {timelineItems.map((it, idx) => {
                              if (it?.eventType === 'MODERATION') {
                                const decision = getModerationDecision(it);
                                const reason = getModerationReason(it);
                                const turnLabel = resolveTurnLabel(it);
                                const seqLabel = resolveSequenceLabel(it);
                                return (
                                  <div key={`${it.sequence}-${idx}`} className="p-4">
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 min-w-0">
                                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex-none">
                                            <ShieldAlert size={16} />
                                          </span>
                                          <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                              <span className="text-sm font-semibold text-gray-800">Moderación</span>
                                              <DecisionBadge decision={decision} />
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-200">
                                                Turno {turnLabel}
                                              </span>
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-200">
                                                Seq {seqLabel}
                                              </span>
                                            </div>
                                            {reason ? (
                                              <div className="mt-1 text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
                                                <span className="font-medium">Razón:</span> {reason}
                                              </div>
                                            ) : (
                                              <div className="mt-1 text-sm text-gray-600">Sin razón disponible.</div>
                                            )}
                                          </div>
                                        </div>

                                        <div className="text-xs text-gray-600 whitespace-nowrap">{formatTs(it.timestamp)}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }

                              const isUser = it.eventType === 'USER_TURN';
                              const label = isUser ? 'Usuario' : 'Agente';
                              const turnLabel = resolveTurnLabel(it);
                              const seqLabel = resolveSequenceLabel(it);

                              // Determinar si hay prompt disponible (solo AI_RESPONSE)
                              const turnKey = isUser ? it.turn : it.parentTurn;
                              const hasPrompt = !isUser && Number.isFinite(turnKey) && promptsByTurnIndex[turnKey];

                              return (
                                <div key={`${it.sequence}-${idx}`} className="p-4">
                                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex items-start gap-3 min-w-0">
                                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full flex-none ${isUser ? 'bg-gray-100 text-gray-700' : 'bg-gray-900 text-white'}`}>
                                          {isUser ? <User size={16} /> : <Bot size={16} />}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-semibold text-gray-800">{label}</span>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                                              Turno {turnLabel}
                                            </span>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                                              Seq {seqLabel}
                                            </span>
                                            {hasPrompt && (
                                              <button
                                                onClick={() => {
                                                  setSelectedPrompt(promptsByTurnIndex[turnKey]);
                                                  setPromptModalTab('system');
                                                  setShowPromptModal(true);
                                                }}
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300 hover:bg-orange-200 transition-colors"
                                                title="Ver prompt enviado al LLM"
                                              >
                                                Ver Prompt
                                              </button>
                                            )}
                                            {!isUser && !hasPrompt && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200" title="No hay audit log para este turno">
                                                Prompt N/A
                                              </span>
                                            )}
                                          </div>
                                          <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
                                            {it.text || ''}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-xs text-gray-600 whitespace-nowrap">{formatTs(it.timestamp)}</div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'zombie' && (
        <ZombieSessionsPanel />
      )}

      {activeSubTab === 'normal' && (
        <NormalSessionsPanel />
      )}

      {activeSubTab === 'public' && (
        <div className="space-y-6">
          {publicStatsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-purple-600" size={40} />
            </div>
          )}
          {publicStatsError && !publicStatsLoading && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-700 mt-0.5" size={18} />
                <p className="text-sm text-red-800">{publicStatsError}</p>
              </div>
            </div>
          )}
          {publicStats && !publicStatsLoading && (
            <>
              {/* Cards de resumen */}
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-purple-200 bg-purple-50 rounded-lg p-2.5">
                  <p className="text-xs text-purple-600 font-medium">Total sesiones</p>
                  <p className="text-2xl font-bold text-purple-900 leading-tight">{publicStats.stats?.totalSessions ?? 0}</p>
                </div>
                <div className="border border-green-200 bg-green-50 rounded-lg p-2.5">
                  <p className="text-xs text-green-600 font-medium">Activas ahora</p>
                  <p className="text-2xl font-bold text-green-900 leading-tight">{publicStats.stats?.activeSessions ?? 0}</p>
                </div>
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-2.5">
                  <p className="text-xs text-blue-600 font-medium">Completadas</p>
                  <p className="text-2xl font-bold text-blue-900 leading-tight">{publicStats.stats?.completedSessions ?? 0}</p>
                </div>
              </div>

              {/* Audit summary */}
              {publicStats.auditSummary && (
                <div className="border border-gray-200 rounded-lg p-3">
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">Audit Logs (TTL 6h)</h4>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                    <span>Prompts: <strong>{publicStats.auditSummary.totalPrompts}</strong></span>
                    <span>Chars: <strong>{publicStats.auditSummary.totalChars?.toLocaleString()}</strong></span>
                    <span>Latencia: <strong>{publicStats.auditSummary.avgLatencyMs} ms</strong></span>
                  </div>
                </div>
              )}

              {/* Por agente */}
              {Array.isArray(publicStats.stats?.byAgent) && publicStats.stats.byAgent.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-800">Por agente / Trial Key</h4>
                  </div>
                  {/* Desktop: tabla */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2 text-gray-600 font-medium">Agente</th>
                          <th className="text-left px-4 py-2 text-gray-600 font-medium">Trial Key</th>
                          <th className="text-right px-4 py-2 text-gray-600 font-medium">Sesiones</th>
                          <th className="text-right px-4 py-2 text-gray-600 font-medium">Completadas</th>
                          <th className="text-right px-4 py-2 text-gray-600 font-medium">Turnos prom.</th>
                          <th className="text-right px-4 py-2 text-gray-600 font-medium">Tokens totales</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {publicStats.stats.byAgent.map((a, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-mono text-xs text-gray-800">{a.agentId || '—'}</td>
                            <td className="px-4 py-2 text-gray-700">{a.trialKey || '—'}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{a.count}</td>
                            <td className="px-4 py-2 text-right text-green-700">{a.completedCount}</td>
                            <td className="px-4 py-2 text-right text-gray-700">{a.avgTurns}</td>
                            <td className="px-4 py-2 text-right text-gray-700">{a.totalTokens?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile: cards */}
                  <div className="sm:hidden divide-y divide-gray-100">
                    {publicStats.stats.byAgent.map((a, i) => (
                      <div key={i} className="px-4 py-3 space-y-1">
                        <p className="font-mono text-xs text-gray-800 truncate">{a.agentId || '—'}</p>
                        <p className="text-xs text-gray-500">{a.trialKey || '—'}</p>
                        <div className="flex gap-4 mt-2 text-xs">
                          <span className="text-gray-600">Sesiones: <strong className="text-gray-900">{a.count}</strong></span>
                          <span className="text-gray-600">Completadas: <strong className="text-green-700">{a.completedCount}</strong></span>
                          <span className="text-gray-600">Turnos: <strong>{a.avgTurns}</strong></span>
                        </div>
                        <p className="text-xs text-gray-500">Tokens: {a.totalTokens?.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sesiones recientes */}
              {Array.isArray(publicStats.stats?.recentSessions) && publicStats.stats.recentSessions.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-800">Sesiones recientes (últimas 30)</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{publicStats.ttlNote}</p>
                  </div>
                  {/* Desktop: tabla */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2 text-gray-600 font-medium">Session ID</th>
                          <th className="text-left px-4 py-2 text-gray-600 font-medium">Trial</th>
                          <th className="text-left px-4 py-2 text-gray-600 font-medium">Estado</th>
                          <th className="text-right px-4 py-2 text-gray-600 font-medium">Turnos</th>
                          <th className="text-right px-4 py-2 text-gray-600 font-medium">Tokens</th>
                          <th className="text-left px-4 py-2 text-gray-600 font-medium">Fin por</th>
                          <th className="text-left px-4 py-2 text-gray-600 font-medium">Expira</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {publicStats.stats.recentSessions.map((s, i) => {
                          const expiresAt = s.expiresAt ? new Date(s.expiresAt) : null;
                          const minsLeft = expiresAt ? Math.round((expiresAt - Date.now()) / 60000) : null;
                          const ttlLabel = minsLeft !== null
                            ? (minsLeft < 0 ? 'Expirada' : `${minsLeft} min`)
                            : '—';
                          return (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-mono text-xs text-gray-600 truncate max-w-[120px]">{s.sessionId}</td>
                              <td className="px-4 py-2 text-gray-700">{s.trialKey || '—'}</td>
                              <td className="px-4 py-2">
                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  s.status === 'active' ? 'bg-green-100 text-green-700' :
                                  s.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>{s.status}</span>
                              </td>
                              <td className="px-4 py-2 text-right text-gray-700">{s.completedTurns}</td>
                              <td className="px-4 py-2 text-right text-gray-700">{s.totalTokens?.toLocaleString()}</td>
                              <td className="px-4 py-2 text-xs text-gray-500">{s.endReason || '—'}</td>
                              <td className={`px-4 py-2 text-xs ${minsLeft !== null && minsLeft < 30 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{ttlLabel}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile: cards */}
                  <div className="sm:hidden divide-y divide-gray-100">
                    {publicStats.stats.recentSessions.map((s, i) => {
                      const expiresAt = s.expiresAt ? new Date(s.expiresAt) : null;
                      const minsLeft = expiresAt ? Math.round((expiresAt - Date.now()) / 60000) : null;
                      const ttlLabel = minsLeft !== null ? (minsLeft < 0 ? 'Expirada' : `${minsLeft} min`) : '—';
                      return (
                        <div key={i} className="px-4 py-3 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-[11px] text-gray-500 truncate flex-1">{s.sessionId}</span>
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                              s.status === 'active' ? 'bg-green-100 text-green-700' :
                              s.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{s.status}</span>
                          </div>
                          <div className="flex gap-3 text-xs text-gray-600 flex-wrap">
                            <span>Trial: <strong>{s.trialKey || '—'}</strong></span>
                            <span>Turnos: <strong>{s.completedTurns}</strong></span>
                            <span>Tokens: <strong>{s.totalTokens?.toLocaleString()}</strong></span>
                          </div>
                          <div className="flex gap-3 text-xs flex-wrap">
                            <span className="text-gray-500">{s.endReason || '—'}</span>
                            <span className={minsLeft !== null && minsLeft < 30 ? 'text-red-600 font-medium' : 'text-gray-400'}>
                              Expira: {ttlLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {publicStats.stats?.totalSessions === 0 && (
                <div className="border border-gray-200 bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-sm text-gray-600">No hay sesiones públicas activas. Las sesiones tienen TTL de 24 horas.</p>
                </div>
              )}
            </>
          )}

          {/* Prompts del trial público */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Prompt Audit Logs (TTL 6h)</h4>
                <p className="text-xs text-gray-500 mt-0.5">Últimos 30 registros · Clic en una fila para ver el contenido del prompt</p>
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={publicPromptsSessionFilter}
                  onChange={e => setPublicPromptsSessionFilter(e.target.value)}
                  placeholder="Filtrar por sessionId..."
                  className="text-xs border border-gray-200 rounded px-2 py-1 w-44 outline-none focus:border-purple-400"
                />
                <button
                  onClick={() => {
                    setPublicPromptsError(null);
                    setPublicPromptsLoading(true);
                    analyticsV4Client.get('/public-trial/prompts', { params: { limit: 30, ...(publicPromptsSessionFilter ? { sessionId: publicPromptsSessionFilter } : {}) } })
                      .then(r => setPublicPrompts(Array.isArray(r.data?.prompts) ? r.data.prompts : []))
                      .catch(e => setPublicPromptsError(e?.response?.data?.message || e?.message))
                      .finally(() => setPublicPromptsLoading(false));
                  }}
                  className="text-xs px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                >
                  Buscar
                </button>
              </div>
            </div>

            {publicPromptsLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader className="animate-spin text-purple-500" size={24} />
              </div>
            )}
            {publicPromptsError && !publicPromptsLoading && (
              <p className="text-xs text-red-600 px-4 py-3">{publicPromptsError}</p>
            )}
            {!publicPromptsLoading && !publicPromptsError && publicPrompts.length === 0 && (
              <p className="text-sm text-gray-500 px-4 py-4">No hay prompts registrados (TTL 6h puede haber expirado).</p>
            )}
            {!publicPromptsLoading && publicPrompts.length > 0 && (
              <>
                {/* Mobile: tarjetas */}
                <div className="sm:hidden divide-y divide-gray-100">
                  {publicPrompts.map((p, i) => (
                    <div
                      key={i}
                      className="px-4 py-3 cursor-pointer hover:bg-purple-50 transition-colors"
                      onClick={() => { setSelectedPrompt(p); setPromptModalTab('system'); setShowPromptModal(true); }}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="font-mono text-[11px] text-gray-500 truncate flex-1">
                          {p.sessionId ? p.sessionId.slice(0, 22) + '…' : '—'}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                          {p.timestamp ? new Date(p.timestamp).toLocaleString('es', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                        <span>Turno: <strong className="text-gray-900">{p.turnIndex ?? '—'}</strong></span>
                        <span>Modelo: <strong className="text-gray-900">{p.model || p.provider || '—'}</strong></span>
                        <span>Msgs: <strong className="text-gray-900">{p.messagesCount ?? (Array.isArray(p.messages) ? p.messages.length : '—')}</strong></span>
                        <span>Tokens: <strong className="text-purple-700">{p.usage?.total_tokens?.toLocaleString() ?? '—'}</strong></span>
                        <span>Latencia: <strong className="text-gray-900">{p.latencyMs != null ? `${p.latencyMs}ms` : '—'}</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop: tabla */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Session</th>
                        <th className="text-right px-4 py-2 text-gray-600 font-medium">Turno</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Modelo</th>
                        <th className="text-right px-4 py-2 text-gray-600 font-medium">Msgs</th>
                        <th className="text-right px-4 py-2 text-gray-600 font-medium">Chars</th>
                        <th className="text-right px-4 py-2 text-gray-600 font-medium">Tokens</th>
                        <th className="text-right px-4 py-2 text-gray-600 font-medium">Latencia</th>
                        <th className="text-left px-4 py-2 text-gray-600 font-medium">Timestamp</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {publicPrompts.map((p, i) => (
                        <tr
                          key={i}
                          className="hover:bg-purple-50 cursor-pointer transition-colors"
                          onClick={() => { setSelectedPrompt(p); setPromptModalTab('system'); setShowPromptModal(true); }}
                        >
                          <td className="px-4 py-2 font-mono text-gray-600 truncate max-w-[110px]" title={p.sessionId}>
                            {p.sessionId ? p.sessionId.slice(0, 20) + '…' : '—'}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-700">{p.turnIndex ?? '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{p.model || p.provider || '—'}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{p.messagesCount ?? (Array.isArray(p.messages) ? p.messages.length : '—')}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{p.charsTotal?.toLocaleString() ?? '—'}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{p.usage?.total_tokens?.toLocaleString() ?? '—'}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{p.latencyMs != null ? `${p.latencyMs}ms` : '—'}</td>
                          <td className="px-4 py-2 text-gray-500">
                            {p.timestamp ? new Date(p.timestamp).toLocaleString('es', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Prompt Layer */}
      {showPromptModal && selectedPrompt && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50" onClick={() => setShowPromptModal(false)}>
          {/* Mobile: bottom sheet full-width h-[95dvh]. Desktop: modal centrado max-w-4xl */}
          <div
            className="bg-white w-full sm:max-w-4xl sm:rounded-lg shadow-xl overflow-hidden flex flex-col"
            style={{ height: '95dvh', borderRadius: '16px 16px 0 0' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle pill — solo mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1 flex-shrink-0">
              <div className="w-9 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Header */}
            <div className="px-3 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Prompt Audit — Turno {selectedPrompt.turnIndex}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
                    {selectedPrompt.phaseId && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-medium">{selectedPrompt.phaseId}</span>}
                    {selectedPrompt.provider && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">{selectedPrompt.provider}/{selectedPrompt.model || '?'}</span>}
                    {selectedPrompt.usage?.total_tokens && <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full">{selectedPrompt.usage.total_tokens} tokens</span>}
                    {Number.isFinite(selectedPrompt.latencyMs) && <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full">{selectedPrompt.latencyMs}ms</span>}
                    {selectedPrompt.payloadHash && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-mono text-[10px]">{selectedPrompt.payloadHash.slice(0, 8)}...</span>}
                  </div>
                </div>
                <button onClick={() => setShowPromptModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 bg-white flex-shrink-0">
              <nav className="flex px-2 sm:px-6 overflow-x-auto" aria-label="Tabs">
                {['system', 'messages', 'raw'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setPromptModalTab(tab)}
                    className={`whitespace-nowrap py-2.5 px-3 border-b-2 font-medium text-sm transition-colors ${
                      promptModalTab === tab
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab === 'system' && 'System Prompt'}
                    {tab === 'messages' && `Mensajes (${selectedPrompt.messagesCount || 0})`}
                    {tab === 'raw' && 'Raw JSON'}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content — ocupa todo el espacio restante */}
            <div className="flex-1 overflow-y-auto px-2 sm:px-6 py-3">
              {promptModalTab === 'system' && (
                <div>
                  {(() => {
                    const systemMsg = Array.isArray(selectedPrompt.messages)
                      ? selectedPrompt.messages.find((m) => m.role === 'system')
                      : null;
                    if (!systemMsg || !systemMsg.content) {
                      return <p className="text-sm text-gray-600 italic">No hay system prompt en este turno</p>;
                    }
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-700">
                            {systemMsg.contentTruncated && <span className="text-amber-600">[TRUNCADO] </span>}
                            {systemMsg.chars ? `${systemMsg.chars} chars` : ''}
                          </span>
                          <button
                            onClick={() => { navigator.clipboard.writeText(systemMsg.content); alert('System prompt copiado al portapapeles'); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded hover:bg-purple-200 transition-colors"
                          >
                            <Copy size={12} />Copiar
                          </button>
                        </div>
                        <pre className="text-[11px] sm:text-xs bg-gray-50 border border-gray-200 rounded p-2 sm:p-4 whitespace-pre-wrap break-words font-mono text-gray-900 leading-relaxed">
                          {systemMsg.content}
                        </pre>
                      </div>
                    );
                  })()}
                </div>
              )}

              {promptModalTab === 'messages' && (
                <div className="space-y-2">
                  {Array.isArray(selectedPrompt.messages) && selectedPrompt.messages.length > 0 ? (
                    selectedPrompt.messages.map((msg, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            msg.role === 'system' ? 'bg-blue-100 text-blue-800' :
                            msg.role === 'user' ? 'bg-gray-200 text-gray-800' :
                            msg.role === 'assistant' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>{msg.role}</span>
                          {msg.contentTruncated && <span className="text-xs text-amber-600 font-medium">[TRUNCADO]</span>}
                          {msg.chars > 0 && <span className="text-xs text-gray-500">{msg.chars} chars</span>}
                        </div>
                        <pre className="text-[11px] sm:text-xs bg-white p-2 whitespace-pre-wrap break-words font-mono text-gray-800 leading-relaxed">
                          {msg.content || '(sin contenido)'}
                        </pre>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-600 italic">Sin mensajes</p>
                  )}
                </div>
              )}

              {promptModalTab === 'raw' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-700">JSON completo</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(JSON.stringify(selectedPrompt, null, 2)); alert('JSON copiado al portapapeles'); }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded hover:bg-purple-200 transition-colors"
                    >
                      <Copy size={12} />Copiar JSON
                    </button>
                  </div>
                  <pre className="text-[11px] sm:text-xs bg-gray-900 text-green-400 rounded p-2 sm:p-4 whitespace-pre-wrap break-words font-mono leading-relaxed">
                    {JSON.stringify(selectedPrompt, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsV4OverviewPanel;
